import { useEffect, useMemo, useState } from "react";
import { MapPin, Clock } from "lucide-react";
import { api } from "@/lib/api";

const BRANCH_STYLES = {
  MC: { label: "MC", cls: "bg-brand/15 text-brand border-brand/40" },
  SC: { label: "SC", cls: "bg-white/5 text-white border-white/20" },
  Both: { label: "All", cls: "bg-brand/5 text-white/80 border-white/15" },
};

function formatDate(iso) {
  try {
    const d = new Date(iso + "T12:00:00");
    return {
      day: d.getDate(),
      month: d.toLocaleString("en-US", { month: "short" }).toUpperCase(),
      year: d.getFullYear(),
    };
  } catch {
    return { day: "--", month: "---", year: "----" };
  }
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/events")
      .then((r) => {
        if (!cancelled) {
          setEvents(r.data || []);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = useMemo(
    () =>
      events.filter((e) =>
        filter === "All" ? true : e.branch === filter || e.branch === "Both",
      ),
    [events, filter],
  );

  return (
    <section
      id="events"
      className="relative py-24 sm:py-32 border-t border-white/5 bg-[#080808]"
      data-testid="events-section"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
          <div className="max-w-xl">
            <div className="text-xs uppercase tracking-[0.35em] text-brand mb-4 flex items-center gap-3">
              <span className="h-px w-10 bg-brand" /> Upcoming
            </div>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-serif leading-[1.02]"
              data-testid="events-title"
            >
              Mark your
              <br />
              <span className="italic text-brand">calendar, queens.</span>
            </h2>
            <p className="mt-5 text-white/60 text-sm leading-relaxed max-w-md">
              For day-to-day updates, follow us on Instagram, Facebook & TikTok.
              These are the signature dates you don't want to miss.
            </p>
          </div>

          <div className="flex gap-2" data-testid="events-filter">
            {["All", "MC", "SC"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-5 h-10 text-[11px] uppercase tracking-[0.25em] border transition-all ${
                  filter === f
                    ? "bg-brand border-brand text-white"
                    : "border-white/15 text-white/70 hover:border-brand hover:text-brand"
                }`}
                data-testid={`events-filter-${f}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4" data-testid="events-list">
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="h-36 bg-white/5 animate-pulse"
                data-testid={`event-skeleton-${i}`}
              />
            ))
          ) : visible.length === 0 ? (
            <div
              className="p-12 border border-white/10 bg-[#0a0a0a] text-center"
              data-testid="events-empty"
            >
              <p className="text-white/60">
                No events match this filter right now. Check our socials for
                updates.
              </p>
            </div>
          ) : (
            visible.map((ev, idx) => {
              const d = formatDate(ev.date_iso);
              const badge = BRANCH_STYLES[ev.branch] || BRANCH_STYLES.Both;
              return (
                <article
                  key={ev.id}
                  className="group relative grid grid-cols-[auto_1fr] gap-6 p-6 lg:p-8 bg-[#0a0a0a] border border-white/10 hover:border-brand/60 hover:bg-[#111] transition-all duration-500"
                  data-testid={`event-item-${idx}`}
                >
                  <div className="flex flex-col items-center justify-center px-6 border-r border-white/10 min-w-[110px]">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-brand">
                      {d.month}
                    </div>
                    <div className="font-serif text-5xl text-white leading-none mt-1">
                      {d.day}
                    </div>
                    <div className="text-[11px] text-white/50 mt-1">
                      {d.year}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`text-[10px] uppercase tracking-[0.25em] px-2 py-1 border ${badge.cls}`}
                      >
                        {ev.category}
                      </span>
                      <span
                        className={`text-[10px] uppercase tracking-[0.25em] px-2 py-1 border ${badge.cls}`}
                      >
                        {badge.label}
                      </span>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-serif text-white group-hover:text-brand transition-colors">
                      {ev.title}
                    </h3>
                    <p className="text-sm text-white/65 leading-relaxed max-w-2xl">
                      {ev.description}
                    </p>
                    <div className="flex flex-wrap gap-5 text-xs text-white/60 pt-1">
                      <span className="inline-flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5 text-brand" />
                        {ev.time_label}
                      </span>
                      <span className="inline-flex items-center gap-2">
                        <MapPin className="w-3.5 h-3.5 text-brand" />
                        {ev.location}
                      </span>
                    </div>
                  </div>
                </article>
              );
            })
          )}
        </div>
      </div>
    </section>
  );
}
